/**
 * A cache to store formatted date and time strings for reuse.
 */
const formatterCache = new Map();

/**
 * Formats a given date into a human-readable string based on the specified time zone.
 *
 * @param {Date} date - The date to format.
 * @param {string} timeZone - The time zone to use for formatting.
 * @returns {string} - The formatted date string.
 */
export const formatBookingDate = (date, timeZone) => {
  const options = {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
    timeZone,
  };

  const cacheKey = `${timeZone}-booking-date-${date.toISOString()}`;

  if (!formatterCache.has(cacheKey)) {
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const dateToFormat = formatter.formatToParts(date);
    const dateFormatted = [
      dateToFormat[0]?.value || '', // day
      ', ', // comma space
      dateToFormat[2]?.value || '', // month
      dateToFormat[3]?.value || '', // space
      dateToFormat[4]?.value || '', // weekday
    ].join('');

    formatterCache.set(cacheKey, dateFormatted);
  }

  return formatterCache.get(cacheKey);
};

/**
 * Formats a given date into a human-readable string with year based on the specified time zone.
 *
 * @param {Date} date - The date to format.
 * @param {string} timeZone - The time zone to use for formatting.
 * @returns {string} - The formatted date with year string.
 */
export const formatBookingDateWithYear = (date, timeZone) => {
  const options = {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
    year: 'numeric',
    timeZone,
  };

  const cacheKey = `${timeZone}-booking-date-year-${date.toISOString()}`;

  if (!formatterCache.has(cacheKey)) {
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const dateToFormat = formatter.formatToParts(date);
    const dateFormatted = [
      dateToFormat[0]?.value || '', // day
      ' ', // space
      dateToFormat[2]?.value || '', // month
      dateToFormat[3]?.value || '', // space
      dateToFormat[4]?.value || '', // weekday
      dateToFormat[5]?.value || '', // optional space
      dateToFormat[6]?.value || '', // optional year
    ].join('');

    formatterCache.set(cacheKey, dateFormatted);
  }

  return formatterCache.get(cacheKey);
};

/**
 * Formats a time range (start and end times) into a human-readable string based on the specified time zone.
 *
 * @param {Date} startDate - The start time of the range.
 * @param {Date} endDate - The end time of the range.
 * @param {string} timeZone - The time zone to use for formatting.
 * @returns {string} - The formatted time range string.
 */
export const formatBookingTimeRange = (startDate, endDate, timeZone) => {
  const cacheKey = `${timeZone}-booking-time-range-${startDate}-${endDate}`;

  if (!formatterCache.has(cacheKey)) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeStyle: 'short',
      timeZone,
    });
    const startTimeToFormat = formatter.formatToParts(startDate);
    const endTimeToFormat = formatter.formatToParts(endDate);
    const timeRangeFormatted = [
      startTimeToFormat[0]?.value || '', // start hour
      startTimeToFormat[1]?.value || '', // start :
      startTimeToFormat[2]?.value || '', // start min
      (startTimeToFormat[4]?.value || '').toLowerCase(), // start am/pm
      ' - ', // separator
      endTimeToFormat[0]?.value || '', // end hour
      endTimeToFormat[1]?.value || '', // end :
      endTimeToFormat[2]?.value || '', // end min
      (endTimeToFormat[4]?.value || '').toLowerCase(), // end am/pm
    ].join('');

    formatterCache.set(cacheKey, timeRangeFormatted);
  }

  return formatterCache.get(cacheKey);
};

/**
 * Formats a given date and time into a short format (mm-dd-yyyy hh:mm) based on the specified time zone.
 *
 * @param {Date} date - The date to format.
 * @param {string} timeZone - The time zone to use for formatting.
 * @returns {string} - The formatted date with year string.
 */
export const shortFormatBookingDateTime = (date, timeZone) => {
  const options = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const dateParts = formatter.formatToParts(new Date(date));
  const dateFormatted = `${dateParts[0]?.value || ''}-` + // month
      `${dateParts[2]?.value || ''}-` + // day
      `${dateParts[4]?.value || ''} ` + // year
      `${dateParts[6]?.value || ''}:` + // hour
      `${dateParts[8]?.value || ''}`; // minute

  return dateFormatted;
}